import { useEffect, useMemo, useState } from "react";
import { Button, Empty, List, Spin, Table } from "antd";
import { DownloadOutlined, FileOutlined } from "@ant-design/icons";
import { useQuery } from "../../common/utils";
import {
  fetchMySaleActivityTeachingActivityDetail,
} from "./api";
import {
  SaleActivityTeachingActivityDetail,
  SaleActivityTeachingSignRecord,
  SaleAttachment,
  TeachingActivityInfo,
} from "./types";
import "./activity.scss";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

function getAttachmentListFromDetail(detail: SaleActivityTeachingActivityDetail | null) {
  const activity = detail?.teachingActivity;
  const list = [
    ...getMeetingScheduleFileList(activity),
    ...(activity?.activityAttachmentList || []),
    ...(activity?.attachmentList || []),
    ...(activity?.teachingActivityAttachmentList || []),
  ];
  const seen = new Set<string>();
  return list.filter((item) => {
    const key = item.id ? String(item.id) : item.fileUrl || item.url || item.shortPathFile || item.fullPathFile || item.fileName || "";
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getFileNameFromUrl(url: string, fallback: string) {
  const pureUrl = url.split("?")[0];
  try {
    return decodeURIComponent(pureUrl.split("/").filter(Boolean).pop() || fallback);
  } catch {
    return pureUrl.split("/").filter(Boolean).pop() || fallback;
  }
}

function getMeetingScheduleFileList(info?: TeachingActivityInfo): SaleAttachment[] {
  if (!info?.meetingScheduleFile) return [];
  return [{
    fileName: getFileNameFromUrl(info.meetingScheduleFile, "会议日程"),
    fileUrl: info.meetingScheduleFile,
    url: info.meetingScheduleFile,
  }];
}

function getDate(value?: string) {
  return value ? value.slice(0, 10) : "";
}

function getTime(value?: string) {
  return value && value.length >= 16 ? value.slice(11, 16) : "";
}

function formatSignTime(label: string, value?: string) {
  if (!value) return "--";
  return `${label}： ${getDate(value)}    ${getTime(value)}`;
}

function isAbsoluteUrl(url: string) {
  return /^https?:\/\//i.test(url);
}

function toAbsoluteUrl(url?: string) {
  if (!url) return "";
  if (isAbsoluteUrl(url)) return url;
  if (url.startsWith("/")) return `${BASE_URL}${url}`;
  return `${BASE_URL}/${url}`;
}

function getAttachmentName(file: SaleAttachment, index: number) {
  return file.fileName || file.name || `附件${index + 1}`;
}

function getAttachmentType(file: SaleAttachment, index: number) {
  const name = getAttachmentName(file, index);
  const suffix = file.fileSuffix || name.split(".").pop() || file.fileUrl?.split(".").pop() || "";
  const type = suffix.split("?")[0].toLowerCase();
  return type === "jpeg" ? "jpg" : type || "file";
}

function getAttachmentUrl(file: SaleAttachment) {
  return file.fullPathFile || file.fileUrl || file.url || file.shortPathFile || "";
}

function formatFileSize(size?: number) {
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)}KB`;
  return `${Math.ceil(size / 1024 / 1024)}M`;
}

export default function SaleActivityOfflineDetail() {
  const query = useQuery();
  const activityDetailId = query.get("activityDetailId") || query.get("saleActivityDetailId") || query.get("teachingActivityId") || "";
  const type = (query.get("type") || "user") as "expert" | "user";
  const [detail, setDetail] = useState<SaleActivityTeachingActivityDetail | null>(null);
  const [attachmentList, setAttachmentList] = useState<SaleAttachment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activityDetailId) return;
    setLoading(true);
    fetchMySaleActivityTeachingActivityDetail(type, activityDetailId)
      .then((res) => {
        const data = res.data || null;
        setDetail(data);
        setAttachmentList(getAttachmentListFromDetail(data));
      })
      .finally(() => setLoading(false));
  }, [activityDetailId, type]);

  const info = detail?.teachingActivity;
  const signRecordList = useMemo(() => detail?.signRecordList || [], [detail]);
  const isExpert = type === "expert";

  const signColumns = [
    {
      title: "场次",
      dataIndex: "teachingActivityClassName",
      key: "teachingActivityClassName",
      render: (value: string, record: SaleActivityTeachingSignRecord) =>
        value || record.teachingActivityName || "--",
    },
    {
      title: "日期",
      key: "date",
      render: (_: any, record: SaleActivityTeachingSignRecord) =>
        getDate(record.signInTime || record.signOutTime) || "--",
    },
    {
      title: "签到",
      dataIndex: "signInTime",
      key: "signInTime",
      render: (value: string) => formatSignTime("签到", value),
    },
    {
      title: "签退",
      dataIndex: "signOutTime",
      key: "signOutTime",
      render: (value: string) => formatSignTime("签退", value),
    },
  ];

  if (!activityDetailId) {
    return <div className="sale-activity-page"><Empty description="缺少线下活动详情参数" /></div>;
  }

  if (!info && !loading) {
    return <div className="sale-activity-page"><Empty description="暂无线下活动详情" /></div>;
  }

  return (
    <div className="sale-activity-page">
      <div className={`sale-activity-detail sale-activity-offline-detail ${isExpert ? "sale-activity-offline-detail--expert" : ""}`}>
        <Spin spinning={loading}>
          <div className="sale-activity-offline-detail__content">
            <div className="sale-activity-offline-detail__attachment">
              {!isExpert && <div className="sale-activity-offline-detail__section-title">附件</div>}
              {attachmentList.length > 0 ? (
                <List
                  className="sale-activity-offline-attachments"
                  dataSource={attachmentList}
                  renderItem={(item, index) => {
                    const name = getAttachmentName(item, index);
                    const typeText = getAttachmentType(item, index).toUpperCase();
                    const fileSize = formatFileSize(item.fileSize);
                    const downloadUrl = toAbsoluteUrl(getAttachmentUrl(item));
                    return (
                      <List.Item
                        className="sale-activity-offline-attachment"
                        actions={[
                          <Button
                            key="download"
                            type="link"
                            icon={<DownloadOutlined />}
                            disabled={!downloadUrl}
                            href={downloadUrl}
                            target="_blank"
                          >
                            下载
                          </Button>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<span className="sale-activity-offline-attachment__icon"><FileOutlined /></span>}
                          title={<span title={name}>{name}</span>}
                          description={[typeText, fileSize].filter(Boolean).join(" · ")}
                        />
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无附件" />
              )}
            </div>
            {!isExpert && (
              <div className="sale-activity-offline-detail__attendance">
                <div className="sale-activity-offline-detail__section-title">考勤统计</div>
                <Table
                  rowKey={(record) => `${record.teachingActivityId || ""}-${record.teachingActivityClassId || ""}-${record.signInTime || ""}-${record.signOutTime || ""}`}
                  columns={signColumns}
                  dataSource={signRecordList}
                  pagination={false}
                  bordered
                  locale={{ emptyText: "暂无考勤统计" }}
                />
              </div>
            )}
          </div>
        </Spin>
      </div>
    </div>
  );
}
